<template>
  <section>
    <nav-component :titulo="'Inventario'" :link="can('productos.registrar') ? { path: '/agregar-producto' } : null" :texto="'Agregar producto'" />
    <b-breadcrumb align="is-left">
      <b-breadcrumb-item tag='router-link' to="/">Inicio</b-breadcrumb-item>
      <b-breadcrumb-item active>Inventario</b-breadcrumb-item>
    </b-breadcrumb>
    <mensaje-inicial :titulo="'No se han encontrado productos :('"
      :subtitulo="'Agrega productos pulsando el botón de Agregar productos'" v-if="productos.length < 1" />

    <div v-if="productos.length > 0">
      <cartas-totales :totales="cartasTotales" />
      <b-field class="busqueda-inventario" label="Buscar producto" expanded>
        <b-input
          v-model="busquedaProducto"
          icon="magnify"
          placeholder="Nombre, código o proveedor"
          type="search"
          expanded
          clearable
        />
      </b-field>
      <div class="columns">
        <div class="column">
          <b-select v-model="perPage" @input="onPerPageChange">
            <option :value="5">5 por página</option>
            <option :value="10">10 por página</option>
            <option :value="15">15 por página</option>
            <option :value="20">20 por página</option>
            <option :value="null">Todos</option>
          </b-select>
        </div>
        <div class="column is-flex is-justify-content-end">
          <b-button type="is-primary" tag="a" :href="printHref" target="__blank" rel="noopener noreferrer">
            Imprimir
          </b-button>
        </div>
      </div>

      <b-table class="box" :data="productosFiltrados" :paginated="isPaginated" :per-page="perPage || productosFiltrados.length || 1"
        :current-page.sync="currentPage" :backend-pagination="false" :total="productosFiltrados.length" :pagination-simple="isPaginationSimple"
        :pagination-position="paginationPosition" :default-sort-direction="defaultSortDirection"
        :pagination-rounded="isPaginationRounded" :sort-icon="sortIcon" :sort-icon-size="sortIconSize"
        default-sort="user.first_name" aria-next-label="Next page" aria-previous-label="Previous page"
        aria-page-label="Page" aria-current-label="Current page">
        <b-table-column field="codigo" label="Código" sortable v-slot="props">
          {{ props.row.codigo }}
        </b-table-column>

        <b-table-column field="nombre" label="Nombre" sortable v-slot="props">
          {{ props.row.nombre }}
        </b-table-column>

        <b-table-column field="vendidoMayoreo" label="¿Mayoreo?" sortable v-slot="props">
          <b-tag type="is-danger" v-if="!props.row.vendidoMayoreo">No</b-tag>

          <div v-if="props.row.vendidoMayoreo">
            <b>Precio: </b>${{ formatoMonto(props.row.precioMayoreo) }}<br>
            <b>A partir: </b>{{ props.row.cantidadMayoreo }}
          </div>
        </b-table-column>

        <b-table-column field="existencia" label="Existencia" sortable v-slot="props">
          <span style="white-space: nowrap;">{{ props.row.existencia }} {{ props.row.unidad }}</span>
        </b-table-column>

        <b-table-column field="vinculacion" label="Producto empaquetado" v-slot="props">
          <div v-if="props.row.paquetes && props.row.paquetes.length" class="is-size-7 has-text-info">
            <div v-for="paq in props.row.paquetes" :key="paq.id_paquete">
              Producto empaquetado: <strong>{{ paq.nombre_paquete }}</strong>
              · 1 = {{ paq.unidades_por_envase }} {{ props.row.unidad }}
              · {{ paq.existencia_paquete }} en stock
            </div>
          </div>
          <div
            v-else-if="props.row.paquetes_de"
            class="is-size-7"
            :class="vinculacionInvalida(props.row) ? 'has-text-danger' : 'has-text-grey'"
          >
            Es producto empaquetado de
            <strong>{{ props.row.paquetes_de.nombre_base }}</strong>
            · 1 = {{ props.row.paquetes_de.unidades_por_envase }}
            {{ props.row.paquetes_de.unidad_base }}
            <span v-if="vinculacionInvalida(props.row)" class="has-text-danger">
              — vinculación inválida: corrígela en el producto
            </span>
          </div>
          <span v-else class="has-text-grey-light">—</span>
        </b-table-column>

        <b-table-column field="nombreAlmacen" label="Almacén" sortable v-slot="props">
          <span v-if="props.row.nombreAlmacen">
            <span class="color-dot" :style="{ backgroundColor: props.row.colorAlmacen || '#ccc' }"></span>
            {{ props.row.nombreAlmacen }}
          </span>
          <span v-else class="has-text-grey-light">—</span>
        </b-table-column>

        <b-table-column field="nombreProveedor" label="Proveedor" sortable v-slot="props">
          {{ props.row.nombreProveedor }}
        </b-table-column>

        <b-table-column field="agregar" label="Agregar" v-slot="props" v-if="can('productos.agregar_existencia')">
          <b-button type="is-primary" @click="agregarExistencia(props.row)">
            <b-icon icon="plus" />
          </b-button>
        </b-table-column>

        <b-table-column field="remover" label="Retirar" v-slot="props" v-if="can('productos.remover_existencia')">
          <b-button type="is-warning" @click="removerExistencia(props.row)">
            <b-icon icon="minus" />
          </b-button>
        </b-table-column>

        <b-table-column field="editar" label="Editar" v-slot="props" v-if="can('productos.editar')">
          <b-button type="is-info" @click="editar(props.row.id)">
            <b-icon icon="pen" />
          </b-button>
        </b-table-column>

        <b-table-column field="eliminar" label="Eliminar" v-slot="props" v-if="can('productos.eliminar')">
          <b-button type="is-danger" @click="eliminar(props.row.id)">
            <b-icon icon="delete" />
          </b-button>
        </b-table-column>
      </b-table>

      <div class="mt-4">
        <b-button type="is-warning" icon-left="eye" @click="abrirOcultos" v-if="can('productos.obtener_ocultos')">
          Productos ocultos
        </b-button>
        <b-button type="is-info" icon-left="package-variant" @click="abrirAuditoria">
          Auditoría de productos empaquetados
        </b-button>
      </div>
    </div>

    <!-- Auditoría de TODAS las vinculaciones de producto empaquetado: permite ver de un
         vistazo cuál está invertida y qué debería hacer, sin abrir producto por producto. -->
    <b-modal v-model="mostrarAuditoria" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: 92vw; max-width: 1100px">
        <header class="modal-card-head has-background-info">
          <p class="modal-card-title has-text-white">Auditoría de productos empaquetados</p>
          <button type="button" class="delete" @click="mostrarAuditoria = false" />
        </header>
        <section class="modal-card-body">
          <p class="is-size-7 has-text-grey">
            "Producto detal" es el que se vende suelto. "Producto empaquetado" es el que se
            abre solo cuando la cantidad pedida supera el stock del producto detal.
            <strong>OK</strong> = funciona · <strong>Sin stock</strong> = no hay productos
            empaquetados que abrir · <strong>Inválida</strong> = 1 producto empaquetado
            rinde menos de 1 unidad del producto detal y hay que corregirla en el producto.
          </p>

          <p v-if="cargandoAuditoria" class="has-text-centered py-4">Cargando…</p>
          <p
            v-else-if="auditoria.length === 0"
            class="has-text-centered has-text-grey py-4"
          >
            No hay vinculaciones de productos empaquetados registradas.
          </p>

          <b-table v-else :data="auditoria" hoverable>
            <b-table-column field="nombre_base" label="Producto detal" v-slot="props">
              {{ props.row.nombre_base }}
              <span class="has-text-grey is-size-7">({{ props.row.codigo_base }})</span>
            </b-table-column>
            <b-table-column field="nombre_paquete" label="Producto empaquetado" v-slot="props">
              {{ props.row.nombre_paquete }}
              <span class="has-text-grey is-size-7">({{ props.row.codigo_paquete }})</span>
            </b-table-column>
            <b-table-column field="unidades_por_envase" label="1 empaquetado =" v-slot="props">
              {{ props.row.unidades_por_envase }} {{ props.row.unidad_base }}
            </b-table-column>
            <b-table-column field="stock_base" label="Stock del producto detal" v-slot="props">
              {{ props.row.stock_base }} {{ props.row.unidad_base }}
            </b-table-column>
            <b-table-column field="stock_envase" label="Empaquetados en stock" v-slot="props">
              {{ props.row.stock_envase }}
            </b-table-column>
            <b-table-column field="estado" label="Estado" v-slot="props">
              <b-tag :type="estadoClase(props.row.estado)">
                {{ textoEstado(props.row.estado) }}
              </b-tag>
            </b-table-column>
            <b-table-column field="explicacion" label="Qué hacer" v-slot="props">
              <span class="is-size-7">{{ props.row.explicacion }}</span>
            </b-table-column>
          </b-table>
        </section>
        <footer class="modal-card-foot">
          <b-button label="Cerrar" @click="mostrarAuditoria = false" />
        </footer>
      </div>
    </b-modal>

    <b-modal v-model="mostrarOcultos" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: 90vw; max-width: 900px">
        <header class="modal-card-head has-background-warning">
          <p class="modal-card-title has-text-white">Productos ocultos (eliminados)</p>
          <button type="button" class="delete" @click="mostrarOcultos = false" />
        </header>
        <section class="modal-card-body">
          <p v-if="productosOcultos.length === 0 && !cargandoOcultos" class="has-text-centered has-text-grey">
            No hay productos ocultos.
          </p>
          <b-table v-if="productosOcultos.length > 0" :data="productosOcultos" hoverable>
            <b-table-column field="codigo" label="Código" v-slot="props">{{ props.row.codigo }}</b-table-column>
            <b-table-column field="nombre" label="Nombre" v-slot="props">{{ props.row.nombre }}</b-table-column>
            <b-table-column field="precioCompra" label="Precio compra" v-slot="props">${{ formatoMonto(props.row.precioCompra) }}</b-table-column>
            <b-table-column field="precioVenta" label="Precio venta" v-slot="props">${{ formatoMonto(props.row.precioVenta) }}</b-table-column>
            <b-table-column field="existencia" label="Existencia" v-slot="props">{{ props.row.existencia }} {{ props.row.unidad }}</b-table-column>
            <b-table-column field="reactivar" label="Reactivar" v-slot="props" v-if="can('productos.reactivar')">
              <b-button type="is-success" size="is-small" @click="reactivar(props.row.id)" :loading="props.row._reactivando">
                <b-icon icon="undo" />
                <span>Reactivar</span>
              </b-button>
            </b-table-column>
          </b-table>
        </section>
        <footer class="modal-card-foot">
          <b-button label="Cerrar" @click="mostrarOcultos = false" />
        </footer>
      </div>
    </b-modal>

    <b-loading :is-full-page="true" v-model="cargando" :can-cancel="false"></b-loading>
  </section>
</template>
<script>
import HttpService from '../../Servicios/HttpService'
import NavComponent from '../Extras/NavComponent'
import MensajeInicial from '../Extras/MensajeInicial'
import CartasTotales from '../Extras/CartasTotales'
import AyudanteSesion from '../../Servicios/AyudanteSesion'
import Utiles from '../../Servicios/Utiles'
import { MAX_CANTIDAD_PRODUCTO } from '@/consts'

export default {
  name: 'ProductosComponent',
  components: { NavComponent, MensajeInicial, CartasTotales },

  data: () => ({
    productos: [],
    productosOcultos: [],
    mostrarOcultos: false,
    cargandoOcultos: false,
    auditoria: [],
    mostrarAuditoria: false,
    cargandoAuditoria: false,
    cargando: false,
    isPaginated: true,
    isPaginationSimple: false,
    isPaginationRounded: true,
    paginationPosition: 'bottom',
    defaultSortDirection: 'asc',
    sortIcon: 'arrow-up',
    sortIconSize: 'is-medium',
    currentPage: 1,
    perPage: 5,
    total: 0,
    busquedaProducto: '',
    cartasTotales: [],
    maxCantidadProducto: MAX_CANTIDAD_PRODUCTO,
  }),

  mounted() {
    this.obtenerProductos()
  },

  computed: {
    printHref() {
      let href = '#/pdf/productos'
      const busqueda = this.busquedaProducto.trim()
      if (!busqueda) return href
      return `${href}?busqueda=${encodeURIComponent(busqueda)}`
    },

    productosFiltrados() {
      const termino = this.busquedaProducto.trim().toLocaleLowerCase()
      if (!termino) return this.productos

      return this.productos.filter(producto => [
        producto.nombre,
        producto.codigo,
        producto.nombreProveedor,
      ].some(valor => String(valor || '').toLocaleLowerCase().includes(termino)))
    },
  },

  watch: {
    busquedaProducto() {
      this.currentPage = 1
    },
  },

  methods: {
    formatoMonto(valor) {
      return Utiles.formatoMonto(valor)
    },
    /** True si la vinculación del producto como envase no puede rendir 1 unidad. */
    vinculacionInvalida(producto) {
      if (!producto || !producto.paquetes_de) return false
      return !Utiles.puedeAbrirEnvase(producto.paquetes_de)
    },
    /** Abre el listado completo de vinculaciones de envase con su estado. */
    abrirAuditoria() {
      this.mostrarAuditoria = true
      this.cargandoAuditoria = true

      HttpService.obtenerConConsultas('productos.php', { accion: 'auditoria_paquetes' })
        .then(resultado => {
          this.auditoria = Array.isArray(resultado) ? resultado : []
        })
        .catch(() => {
          this.auditoria = []
          this.$buefy.toast.open({
            type: 'is-danger',
            message: 'No se pudo cargar la auditoría de productos empaquetados',
          })
        })
        .finally(() => {
          this.cargandoAuditoria = false
        })
    },
    estadoClase(estado) {
      if (estado === 'OK') return 'is-success'
      if (estado === 'SIN_STOCK_ENVASE') return 'is-warning'
      return 'is-danger'
    },
    textoEstado(estado) {
      if (estado === 'OK') return 'OK'
      if (estado === 'SIN_STOCK_ENVASE') return 'Sin productos empaquetados'
      return 'Inválida'
    },
    cantidadProductoValida(cantidad, maximo = this.maxCantidadProducto) {
      const valor = Number(cantidad)
      return Number.isFinite(valor) && valor > 0 && valor <= Number(maximo)
    },
    agregarExistencia(producto) {
      this.$buefy.dialog.prompt({
        message: '¿Cuántas piezas vas a agregar de ' + producto.nombre + '?',
        cancelText: 'Cancelar',
        confirmText: 'Agregar',
        inputAttrs: {
          type: 'number',
          placeholder: 'Escribe la cantidad de productos',
          value: '',
          step: '0.01',
          min: 0.000,
          max: this.maxCantidadProducto,
        },
        trapFocus: true,
        onConfirm: (value) => {
          if (!this.cantidadProductoValida(value)) {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'La cantidad debe ser mayor a 0 y menor o igual a ' + this.maxCantidadProducto,
            })
            return
          }

          this.cargando = true
          const precioCompra = Number(producto.precioCompra) * 100
          const monto = (Number(value) * precioCompra) / 100

          HttpService.registrar('productos.php', {
            accion: 'agregar_existencia',
            entrada: {
              cantidad: value,
              id: producto.id,
              usuario: AyudanteSesion.usuario().id,
              monto,
            },
          })
            .then(registrado => {
              if (registrado) {
                this.cargando = false
                this.$buefy.toast.open(value + ' Productos agregados a ' + producto.nombre)
                this.obtenerProductos()
              }
            })

        }
      })
    },

    removerExistencia(producto) {
      this.$buefy.dialog.prompt({
        message: '¿Cuántas unidades (' + producto.unidad + ') vas a retirar de ' + producto.nombre + '?',
        cancelText: 'Cancelar',
        confirmText: 'Retirar',
        inputAttrs: {
          type: 'number',
          placeholder: 'Escribe la cantidad de productos',
          value: '',
          min: 0.000,
          step: 0.01,
          max: producto.existencia,
        },
        trapFocus: true,
        onConfirm: (value) => {
          if (!this.cantidadProductoValida(value, producto.existencia)) {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'La cantidad debe ser mayor a 0 y no puede superar la existencia disponible',
            })
            return
          }

          this.cargando = true
          HttpService.registrar('productos.php', {
            accion: 'remover_existencia',
            producto: {
              cantidad: value,
              id: producto.id,
              usuario: AyudanteSesion.usuario().id,
            },
          })
            .then(registrado => {
              if (registrado) {
                this.cargando = false
                this.$buefy.toast.open(value + ' Productos retirados a ' + producto.nombre)
                this.obtenerProductos()
              }
            })

        }
      })
    },

    async eliminar(idProducto) {
      this.$buefy.dialog.confirm({
        title: 'Eliminar producto',
        message: 'Seguro que quieres <b>eliminar</b> este producto? Esta acción no se puede revertir.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.cargando = true
          HttpService.eliminar('productos.php', {
            accion: 'eliminar',
            id: idProducto
          })
            .then(resultado => {
              if (!resultado) {
                this.$buefy.toast.open('Error al eliminar')
                this.cargando = false
                return
              }

              if (resultado) {
                this.cargando = false
                this.$buefy.toast.open({
                  type: 'is-info',
                  message: 'Producto eliminado.'
                })
                this.obtenerProductos()
              }
            })
        }
      })
    },

    editar(idProducto) {
      this.$router.push({
        name: "EditarProducto",
        params: { id: idProducto }
      })
    },

    obtenerProductos() {
      this.cargando = true
      let payload = {
        accion: 'obtener',
        pagina: null,
        limite: null,
      }
      HttpService.obtenerConConsultas('productos.php', payload)
        .then(respuesta => {
          this.productos = respuesta.productos
          this.total = respuesta.totalProductosGeneral || respuesta.total || respuesta.productos.length

          this.cartasTotales = [
            { nombre: "Número Productos", total: this.total, icono: "package-variant-closed", clase: "has-text-danger" },
            { nombre: "Total en existencia", total: respuesta.totalProductos, icono: "chart-bar-stacked", clase: "has-text-primary" },
            { nombre: "Total inventario", total: respuesta.totalInventario, icono: "currency-usd", clase: "has-text-success" },
          ]
          this.cargando = false
        })
    },

    onPerPageChange() {
      this.currentPage = 1
    },

    abrirOcultos() {
      this.mostrarOcultos = true
      this.cargandoOcultos = true
      let payload = { accion: 'obtener_ocultos' }
      HttpService.obtenerConConsultas('productos.php', payload)
        .then(productos => {
          this.productosOcultos = (productos || []).map(p => ({ ...p, _reactivando: false }))
          this.cargandoOcultos = false
        })
    },

    reactivar(id) {
      const idx = this.productosOcultos.findIndex(p => p.id === id)
      if (idx >= 0) this.productosOcultos[idx]._reactivando = true

      HttpService.eliminar('productos.php', { accion: 'reactivar', id })
        .then(resultado => {
          if (resultado) {
            this.productosOcultos.splice(idx, 1)
            this.$buefy.toast.open({ type: 'is-success', message: 'Producto reactivado con éxito.' })
            this.obtenerProductos()
          } else {
            if (idx >= 0) this.productosOcultos[idx]._reactivando = false
            this.$buefy.toast.open({ type: 'is-danger', message: 'Error al reactivar producto.' })
          }
        })
    }
  }
}
</script>

<style scoped>
.busqueda-inventario {
  width: 100%;
  margin-bottom: 1rem;
}

.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  border: 1px solid rgba(0,0,0,0.1);
  vertical-align: middle;
}

@media (max-width: 767px) {
  .busqueda-inventario {
    margin-bottom: 0.75rem;
  }

  .columns {
    margin-left: 0;
    margin-right: 0;
  }

  .columns .column {
    padding: 0.5rem 0;
  }

  .columns .column.is-flex {
    justify-content: flex-start !important;
  }
}
</style>
